/**
 * Pure helpers shared by `BoostAnalyticsProvider`, `transport`, and `cartToken`.
 * Colocated because each is <30 LOC, has no async or side effects beyond its
 * own scope, and they're consumed as a set. Splitting them into one file
 * each (session.ts, rid.ts, pageType.ts, consent.ts) added 3 unnecessary
 * imports without improving testability.
 */
/** Boost's shared session-id key — reusing it joins our events to the session
 *  Boost's own app records elsewhere on the storefront. */
const BOOST_SESSION_STORAGE_KEY = 'boostSdSessionId';
/** Our own, Boost-independent session key — used when the flag below is set. */
const OWN_SESSION_STORAGE_KEY = 'mrSdSessionId';
/**
 * `BOOST_ANALYTICS_USE_OWN_SESSION_KEY=true` (in `.env`, baked at build time by
 * dotenv-webpack like `BOOST_ANALYTICS_ENDPOINT`) switches session tracking to
 * our own key and skips Boost's `getSessionId()` accessor, giving us a session
 * independent of the Boost app. Default (unset/false): share `boostSdSessionId`
 * so attribution joins up.
 */
const useOwnSessionKey = process.env.BOOST_ANALYTICS_USE_OWN_SESSION_KEY === 'true';
const SESSION_STORAGE_KEY = useOwnSessionKey ? OWN_SESSION_STORAGE_KEY : BOOST_SESSION_STORAGE_KEY;
/**
 * RFC-4122 v4 UUID. Uses `crypto.randomUUID()` where available (modern
 * browsers + jsdom 21+); falls back to the same Math.random pattern Boost's
 * own bundle uses (see `bc-widget-integration.js` v1.48.4) so behaviour
 * matches across environments.
 */
export function uuidv4() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}
/**
 * Generate a fresh client-side request id. Format matches Boost's bundle
 * (`'rid-' + uuid`) so server-side correlation works even when Boost's API
 * doesn't echo back a `meta.rid` of its own.
 */
export function generateClientRid() {
    return `rid-${uuidv4()}`;
}
/**
 * Read (or lazily create) the per-visitor Boost session id.
 *
 * Accessor-first, per Boost's dev doc, which gives two ways to obtain `sid`:
 * `window.boostWidgetIntegration.getSessionId()` or the `boostSdSessionId`
 * localStorage key. We prefer the accessor when Boost's widget bundle is on
 * the page: it returns the canonical id Boost's own events use, so ours stay
 * in the same session even if Boost later changes how/where the id is stored.
 * The bundle isn't guaranteed to be loaded on a headless storefront, so we
 * fall back to reading the documented localStorage key directly (lazily
 * creating it) — which is also why the quote-stripping shim below exists.
 *
 * No TTL — the session persists across visits. Swallows storage failures
 * (private browsing, quota exceeded) and returns a non-persistent UUID so the
 * rest of the analytics pipeline still has a value; the trade-off is that
 * those sessions can't be joined to a returning visitor's later events.
 */
export function getBoostSessionId() {
    var _a, _b;
    // When sharing Boost's key, prefer its canonical accessor if the bundle is
    // present. In own-key mode we skip it deliberately to stay independent.
    if (!useOwnSessionKey) {
        try {
            const fromBundle = (_b = (_a = window.boostWidgetIntegration) === null || _a === void 0 ? void 0 : _a.getSessionId) === null || _b === void 0 ? void 0 : _b.call(_a);
            if (typeof fromBundle === 'string' && fromBundle.length > 0)
                return fromBundle;
        }
        catch (_c) {
            /* accessor unavailable or threw — use the localStorage fallback */
        }
    }
    try {
        const existing = localStorage.getItem(SESSION_STORAGE_KEY);
        if (existing) {
            // Boost's bundle may persist the id JSON-quoted; strip wrapping quotes
            // (matching the bundle's own `getSessionId`) so our `sid` joins to the
            // same session rather than sending a `"\"…\""` value.
            return existing.startsWith('"') && existing.endsWith('"') && existing.length > 2
                ? existing.slice(1, -1)
                : existing;
        }
        const fresh = uuidv4();
        localStorage.setItem(SESSION_STORAGE_KEY, fresh);
        return fresh;
    }
    catch (_d) {
        return uuidv4();
    }
}
/**
 * True when Boost's own widget bundle is live on the page, detected via its
 * `getSessionId()` accessor. We use this to decide whether a click event can be
 * handed to Boost's persist-and-resend buffer (which only Boost's bundle
 * flushes, on the next page) instead of being sent immediately.
 *
 * Deliberately does NOT fall back to the `boostSdSessionId` localStorage key:
 * `getBoostSessionId()` writes that key itself, so its presence wouldn't prove
 * Boost's bundle is loaded — only the accessor does.
 */
export function hasBoostSession() {
    var _a, _b;
    try {
        const fromBundle = (_b = (_a = window.boostWidgetIntegration) === null || _a === void 0 ? void 0 : _a.getSessionId) === null || _b === void 0 ? void 0 : _b.call(_a);
        return typeof fromBundle === 'string' && fromBundle.length > 0;
    }
    catch (_c) {
        return false;
    }
}
/**
 * Map `window.location.pathname` to Boost's page-type enum. Defaults to
 * `home_page` for unknown paths. `template.name` (Shopify Liquid) is a
 * tie-breaker but isn't reliably exposed to JS across themes, so we don't
 * depend on it.
 */
export function detectPageType() {
    const path = window.location.pathname;
    if (path === '/' || path === '')
        return 'home_page';
    // Check products before collections: Shopify renders the product template
    // for `/collections/<handle>/products/<handle>`, so a `/products/` segment
    // anywhere means a product page.
    if (path.includes('/products/'))
        return 'product_page';
    if (path.startsWith('/collections/'))
        return 'collection_page';
    // Exact/segment match so `/search-tips` etc. don't count as search.
    if (path === '/search' || path.startsWith('/search/'))
        return 'search_page';
    if (path.startsWith('/cart'))
        return 'cart_page';
    if (path.startsWith('/checkout'))
        return 'checkout_page';
    return 'home_page';
}
/**
 * Consent gate. Reads Shopify's standard `customerPrivacy.userCanBeTracked`
 * API when present, defaults to allow when absent (most Aquila stores don't
 * use Shopify's consent infrastructure yet — future-proofs without code
 * changes).
 */
export function canTrack() {
    var _a;
    const cp = (_a = window.Shopify) === null || _a === void 0 ? void 0 : _a.customerPrivacy;
    if (!cp)
        return true;
    if (typeof cp.userCanBeTracked === 'function') {
        try {
            return cp.userCanBeTracked();
        }
        catch (_b) {
            return true;
        }
    }
    return true;
}
/**
 * Best-effort logged-in customer id. Both globals are populated by Shopify on
 * logged-in storefronts. Returns `undefined` for guests.
 */
export function getCustomerId() {
    var _a, _b, _c, _d;
    const w = window;
    const cid = (_b = (_a = w.__st) === null || _a === void 0 ? void 0 : _a.cid) !== null && _b !== void 0 ? _b : (_d = (_c = w.meta) === null || _c === void 0 ? void 0 : _c.page) === null || _d === void 0 ? void 0 : _d.customerId;
    return cid != null ? String(cid) : undefined;
}
/**
 * Tenant id — the Shopify shop domain. Boost's backend uses this to route
 * incoming events to the right store's analytics dashboard.
 */
export function getTenantId() {
    var _a, _b;
    const w = window;
    return (_b = (_a = w.Shopify) === null || _a === void 0 ? void 0 : _a.shop) !== null && _b !== void 0 ? _b : '';
}
/**
 * Best-effort current collection id, for the `collection_scope` part of a
 * filter/collection event's `qs`. Prefers Boost's own
 * `boostWidgetIntegration.generalSettings.collection_id`; falls back to
 * Shopify's `meta.page` (resourceType 'collection'). Returns `undefined` when
 * not on a collection.
 */
export function getCollectionId() {
    var _a, _b, _c, _d, _e, _f;
    const w = window;
    const fromBoost = (_b = (_a = w.boostWidgetIntegration) === null || _a === void 0 ? void 0 : _a.generalSettings) === null || _b === void 0 ? void 0 : _b.collection_id;
    if (fromBoost != null && fromBoost !== '')
        return String(fromBoost);
    const page = (_d = (_c = w.meta) === null || _c === void 0 ? void 0 : _c.page) !== null && _d !== void 0 ? _d : (_f = (_e = w.ShopifyAnalytics) === null || _e === void 0 ? void 0 : _e.meta) === null || _f === void 0 ? void 0 : _f.page;
    if ((page === null || page === void 0 ? void 0 : page.resourceType) === 'collection' && (page === null || page === void 0 ? void 0 : page.resourceId) != null)
        return String(page.resourceId);
    return undefined;
}
/**
 * Boost's own cart token, written to `localStorage['boost_cart_token']` by Boost's
 * app. Boost uses it for more accurate revenue attribution; we forward it as `bct`
 * on add-to-cart events (Option 1 of Boost's conversion-tracking guidance — see
 * docs/boost/analytics.md). Returns `undefined` when absent or storage is
 * unavailable, so the field is stripped from the wire. Strips wrapping quotes for
 * the same reason `getBoostSessionId` does — Boost's bundle may persist it JSON-quoted.
 */
const BOOST_CART_TOKEN_KEY = 'boost_cart_token';
export function getBoostCartToken() {
    try {
        const token = localStorage.getItem(BOOST_CART_TOKEN_KEY);
        if (!token)
            return undefined;
        return token.startsWith('"') && token.endsWith('"') && token.length > 2
            ? token.slice(1, -1)
            : token;
    }
    catch (_a) {
        return undefined;
    }
}
/**
 * localStorage key for the click→add-to-cart correlation carryover. When a
 * product is clicked on our custom PLP/search we remember its Boost `rid` here,
 * so a later add-to-cart can send the documented `rid` — including an ATC fired
 * from the Shopify *theme* PDP (a separate codebase) after the shopper navigates
 * there, where the search response and its `rid` are otherwise gone. The theme's
 * `boost-atc-tracker.js` reads this exact key.
 *
 * Deliberately NOT Boost's own `boostSdProductIdsClicked`: writing there could
 * make Boost's bundle ALSO fire the ATC event → duplicates.
 */
const CLICKED_PRODUCTS_KEY = 'mrBoostClickedProducts';
const CLICKED_PRODUCTS_MAX = 30;
/**
 * Remember a clicked product's server `rid` (+ action context) keyed by product
 * id, for later add-to-cart attribution. Caps the map and prunes oldest entries.
 * Best-effort: swallows storage failures.
 */
export function rememberProductClick(productId, rid, action) {
    try {
        const map = JSON.parse(localStorage.getItem(CLICKED_PRODUCTS_KEY) || '{}');
        map[productId] = { rid, a: action, t: Date.now() };
        const keys = Object.keys(map);
        if (keys.length > CLICKED_PRODUCTS_MAX) {
            keys.sort((a, b) => map[a].t - map[b].t)
                .slice(0, keys.length - CLICKED_PRODUCTS_MAX)
                .forEach(k => delete map[k]);
        }
        localStorage.setItem(CLICKED_PRODUCTS_KEY, JSON.stringify(map));
    }
    catch (_a) {
        /* storage unavailable — carryover is best-effort */
    }
}
